import React, { useEffect, useState } from 'react';
import { Typography, useTheme, Alert, Card, CardContent, Divider, List, ListItem, ListItemText } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { Box } from '@mui/system';
import { CommonLayout } from './commonComponents';
import { FilePond, registerPlugin } from 'react-filepond';
import 'filepond/dist/filepond.min.css';
import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size';
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';
import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css';
import { useSnackbar } from 'notistack';

registerPlugin(
    FilePondPluginFileValidateSize,
    FilePondPluginFileValidateType
);

interface UploadResult {
    success: boolean;
    message: string;
    failedRecords?: Array<{
        row: number;
        error: string;
        data: Record<string, any>;
    }>;
    successCount?: number;
    totalCount?: number;
}

// Upload RM Mapping component with CSV/XLSX support
export default function uploadRMMapping(): JSX.Element {
    const theme = useTheme();
    const { enqueueSnackbar } = useSnackbar();
    const [files, setFiles] = useState<any[]>([]);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);
    const [showResult, setShowResult] = useState(false);

    // Handle file upload to API
    const handleUpload = async () => {
        if (files.length === 0) {
            enqueueSnackbar('Please select a file to upload', { variant: 'warning' });
            return;
        }

        setIsUploading(true);
        const formData = new FormData();
        formData.append('file', files[0].file);

        try {
            // Replace with your actual API endpoint
            const response = await fetch('/api/onboarding/iwapp-users/updateRmMappingCSV', {
                method: 'POST',
                body: formData,
                headers: {
                    // Add any required headers like authorization
                    // 'Authorization': `Bearer ${token}`,
                },
            });

            const result = await response.json();

            if (response.ok) {
                setUploadResult({
                    success: true,
                    message: result.message || 'File uploaded successfully',
                    successCount: result.successCount,
                    totalCount: result.totalCount,
                    failedRecords: result.failedRecords || []
                });
                enqueueSnackbar('File uploaded successfully!', { variant: 'success' });
            } else {
                setUploadResult({
                    success: false,
                    message: result.message || 'Upload failed',
                    failedRecords: result.failedRecords || []
                });
                enqueueSnackbar('Upload failed. Please check the results.', { variant: 'error' });
            }
        } catch (error) {
            console.error('Upload error:', error);
            setUploadResult({
                success: false,
                message: 'Network error occurred during upload',
            });
            enqueueSnackbar('Network error occurred', { variant: 'error' });
        } finally {
            setIsUploading(false);
            setShowResult(true);
        }
    };

    // Clear results and reset for new upload
    const handleClear = () => {
        setFiles([]);
        setUploadResult(null);
        setShowResult(false);
        setIsUploading(false);
    };

    // Download failed records as CSV
    const downloadFailedRecords = () => {
        if (!uploadResult?.failedRecords?.length) return;

        const csvContent = [
            ['Row', 'Error', 'Data'],
            ...uploadResult.failedRecords.map(record => [
                record.row,
                record.error,
                JSON.stringify(record.data)
            ])
        ].map(row => row.join(',')).join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'failed-records.csv';
        a.click();
        window.URL.revokeObjectURL(url);
    };

    return (
        <CommonLayout>
            <Box sx={{ maxWidth: 800, margin: '0 auto', p: 3 }}>
                {/* Header */}
                <Box sx={{ mb: 4, textAlign: 'center' }}>
                    <Typography
                        variant="h4"
                        sx={{
                            color: 'primary.main',
                            fontWeight: 600,
                            mb: 1
                        }}
                    >
                        Upload RM Mapping
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Upload CSV or XLSX files to update RM mapping data
                    </Typography>
                </Box>

                {/* Upload Section */}
                {!showResult && (
                    <Card sx={{ mb: 3 }}>
                        <CardContent>
                            <Typography variant="h6" sx={{ mb: 2 }}>
                                Select File
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                Supported formats: CSV (.csv), Excel (.xlsx, .xls)
                            </Typography>

                            <FilePond
                                files={files}
                                onupdatefiles={setFiles}
                                allowMultiple={false}
                                maxFiles={1}
                                acceptedFileTypes={['text/csv', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel']}
                                maxFileSize="10MB"
                                labelIdle='Drag & Drop your CSV or XLSX file or <span class="filepond--label-action">Browse</span>'
                                labelFileTypeNotAllowed="File type not allowed"
                                fileValidateTypeLabelExpectedTypes="Expects CSV or XLSX files"
                            />

                            <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'center' }}>
                                <LoadingButton
                                    loadingPosition="start"
                                    loading={isUploading}
                                    variant="contained"
                                    onClick={handleUpload}
                                    disabled={files.length === 0 || isUploading}
                                    sx={{
                                        minWidth: 120,
                                        color: 'textDark.primary',
                                        backgroundColor: 'primary.500',
                                        padding: "10px",
                                        ...theme.typography.body_600
                                    }}
                                >
                                    {isUploading ? 'Uploading...' : 'Upload File'}
                                </LoadingButton>
                                <LoadingButton
                                    variant="outlined"
                                    onClick={handleClear}
                                    disabled={isUploading}
                                    sx={{
                                        color: 'text.secondary',
                                        backgroundColor: 'transparent',
                                        borderColor: 'text.secondary',
                                        borderWidth: '0.5px',
                                        borderStyle: 'solid',
                                        padding: "10px",
                                        "&:hover, &.Mui-focusVisible": {
                                            borderColor: 'text.secondary',
                                            backgroundColor: "background.default"
                                        },
                                        ...theme.typography.body_600
                                    }}
                                >
                                    Clear
                                </LoadingButton>
                            </Box>
                        </CardContent>
                    </Card>
                )}

                {/* Results Section */}
                {showResult && uploadResult && (
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="h6">
                                    Upload Results
                                </Typography>
                                <LoadingButton
                                    variant="outlined"
                                    onClick={handleClear}
                                    sx={{
                                        fontSize: '0.875rem',
                                        padding: '6px 16px',
                                        color: 'text.secondary',
                                        backgroundColor: 'transparent',
                                        borderColor: 'text.secondary',
                                        borderWidth: '0.5px',
                                        borderStyle: 'solid',
                                        "&:hover, &.Mui-focusVisible": {
                                            borderColor: 'text.secondary',
                                            backgroundColor: "background.default"
                                        },
                                        ...theme.typography.body_600
                                    }}
                                >
                                    Upload New File
                                </LoadingButton>
                            </Box>

                            <Alert
                                severity={uploadResult.success ? 'success' : 'error'}
                                sx={{ mb: 3 }}
                            >
                                {uploadResult.message}
                            </Alert>

                            {/* Success Summary */}
                            {uploadResult.success && (
                                <Box sx={{ mb: 3 }}>
                                    <Typography variant="body1" sx={{ mb: 1 }}>
                                        <strong>Upload Summary:</strong>
                                    </Typography>
                                    <Typography variant="body2">
                                        Successfully processed: {uploadResult.successCount || 0} records
                                    </Typography>
                                    <Typography variant="body2">
                                        Total records: {uploadResult.totalCount || 0}
                                    </Typography>
                                </Box>
                            )}

                            {/* Failed Records */}
                            {uploadResult.failedRecords && uploadResult.failedRecords.length > 0 && (
                                <Box>
                                    <Divider sx={{ my: 2 }} />
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                        <Typography variant="h6" color="error">
                                            Failed Records ({uploadResult.failedRecords.length})
                                        </Typography>
                                        <LoadingButton
                                            variant="outlined"
                                            onClick={downloadFailedRecords}
                                            sx={{
                                                fontSize: '0.875rem',
                                                padding: '6px 16px',
                                                color: 'text.secondary',
                                                backgroundColor: 'transparent',
                                                borderColor: 'text.secondary',
                                                borderWidth: '0.5px',
                                                borderStyle: 'solid',
                                                "&:hover, &.Mui-focusVisible": {
                                                    borderColor: 'text.secondary',
                                                    backgroundColor: "background.default"
                                                },
                                                ...theme.typography.body_600
                                            }}
                                        >
                                            Download Failed Records
                                        </LoadingButton>
                                    </Box>

                                    <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                                        <List dense>
                                            {uploadResult.failedRecords.slice(0, 10).map((record, index) => (
                                                <ListItem key={index} divider>
                                                    <ListItemText
                                                        primary={`Row ${record.row}: ${record.error}`}
                                                        secondary={`Data: ${JSON.stringify(record.data)}`}
                                                        primaryTypographyProps={{ color: 'error' }}
                                                    />
                                                </ListItem>
                                            ))}
                                        </List>
                                        {uploadResult.failedRecords.length > 10 && (
                                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
                                                ... and {uploadResult.failedRecords.length - 10} more records. Download the full list above.
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                )}
            </Box>
        </CommonLayout>
    );
}